import { Check, Plus } from 'lucide-react'
import { useState } from 'react'

import type { UserBrief } from '@/api/types'
import { Dialog } from '@/components/ui/Dialog'
import { ErrorState } from '@/components/ui/EmptyState'
import { LockBadge } from '@/components/ui/LockBadge'
import { CenteredSpinner } from '@/components/ui/Spinner'
import { useI18n } from '@/i18n'
import { cn } from '@/lib/cn'

import { useCreateList, useListMembership, useMyLists } from '../hooks/useLists'
import { ListForm } from './ListForm'

interface Props {
  user: UserBrief
  open: boolean
  onClose: () => void
}

/**
 * 从用户页把人加进 / 移出我的列表。
 *
 * 新建列表不另开一个对话框（两层遮罩叠在一起，Esc 会一次关掉两层），
 * 而是在同一个对话框里切到表单；建好之后顺手把这个人加进去。
 */
export function AddToListDialog({ user, open, onClose }: Props) {
  const { t, tp } = useI18n()
  const lists = useMyLists(user.username)
  const membership = useListMembership()
  const create = useCreateList()
  const [creating, setCreating] = useState(false)

  if (creating) {
    return (
      <Dialog open={open} onClose={onClose} title={t('lists.create')}>
        <div className="px-4 pb-5">
          <ListForm
            submitLabel={t('lists.createAndAdd', { username: user.username })}
            pending={create.isPending || membership.isPending}
            error={create.error}
            onSubmit={(input) =>
              create.mutate(input, {
                onSuccess: ({ list }) => {
                  membership.mutate({ listId: list.id, username: user.username, member: false })
                  setCreating(false)
                },
              })
            }
          />
        </div>
      </Dialog>
    )
  }

  return (
    <Dialog open={open} onClose={onClose} title={t('lists.addTitle', { username: user.username })}>
      <div className="pb-3">
        <button
          type="button"
          onClick={() => setCreating(true)}
          className="flex w-full items-center gap-3 px-4 py-3 text-left font-bold text-brand-ink hover:bg-surface-hover"
        >
          <Plus className="size-5" /> {t('lists.create')}
        </button>
        {lists.isPending && <CenteredSpinner />}
        {lists.isError && (
          <ErrorState message={lists.error.message} onRetry={() => lists.refetch()} />
        )}
        {lists.data?.length === 0 && (
          <p className="px-4 py-3 text-[15px] text-ink-muted">{t('lists.noneYet')}</p>
        )}
        <ul>
          {lists.data?.map((list) => {
            const member = Boolean(list.viewer.has_member)
            const busy = membership.isPending && membership.variables?.listId === list.id
            return (
              <li key={list.id}>
                <button
                  type="button"
                  role="checkbox"
                  aria-checked={member}
                  disabled={busy}
                  onClick={() =>
                    membership.mutate({ listId: list.id, username: user.username, member })
                  }
                  className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-surface-hover disabled:opacity-60"
                >
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-1 font-bold">
                      <span className="truncate">{list.name}</span>
                      {list.is_private && <LockBadge />}
                    </span>
                    <span className="block text-[13px] text-ink-muted">
                      {tp('lists.memberCount', list.member_count)}
                    </span>
                  </span>
                  <span
                    aria-hidden
                    className={cn(
                      'flex size-6 shrink-0 items-center justify-center rounded-sm border-2',
                      member ? 'border-brand-ink bg-brand text-on-brand' : 'border-line-strong',
                    )}
                  >
                    {member && <Check className="size-4" strokeWidth={3} />}
                  </span>
                </button>
              </li>
            )
          })}
        </ul>
      </div>
    </Dialog>
  )
}
